import React, { useEffect, useState } from 'react';

const WeatherSlider = () => {
  const [retrievedForcast, setRetrievedForcast] = useState([]),
    [showMore, setShowMore] = useState(false),
    key = "cbbe0d7e6a7349e3b17230356202811",
    baseUrl = "https://api.weatherapi.com/v1/";

  const parseDate = (dateString) =>
    /^\d{4}-\d{2}-\d{2}$/.test(dateString || "")
      ? new Date(`${dateString}T12:00:00`)
      : new Date(dateString);

  const formatTimeLabel = (dateString) =>
    new Date(dateString)
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/\s/g, "");

  const getDayLabel = (dateString) =>
    parseDate(dateString)
      .toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase();

  const getFullDateLabel = (dateString) =>
    parseDate(dateString)
      .toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });

  const getWeatherEmoji = (conditionText = "", isDay = 1) => {
    const text = conditionText.toLowerCase();
    if (text.includes("thunder")) return "⛈️";
    if (text.includes("blizzard") || text.includes("snow") || text.includes("sleet")) return "🌨️";
    if (text.includes("drizzle")) return "🌦️";
    if (text.includes("rain") || text.includes("shower")) return "🌧️";
    if (text.includes("mist") || text.includes("fog") || text.includes("haze")) return "🌫️";
    if (text.includes("partly")) return isDay ? "⛅" : "☁️";
    if (text.includes("cloud") || text.includes("overcast")) return "☁️";
    if (text.includes("wind")) return "💨";
    if (text.includes("sunny") || text.includes("clear")) return isDay ? "☀️" : "🌙";
    return isDay ? "☀️" : "🌙";
  };

  const handleDisplay = (forecasts) => {
    const currentWithForecast = forecasts.current
      ? { ...forecasts.current, forecast: forecasts.forecast.forecastday[0] }
      : forecasts.current;
    setRetrievedForcast([
      currentWithForecast,
      ...forecasts.forecast.forecastday.slice(1),
    ]);
  };

  const getForecast = async (location) => {
    const response = await fetch(`${baseUrl}forecast.json?key=${key}&q=${location}&days=5`),
      forecasts = await response.json();

    handleDisplay(forecasts);
  };

  useEffect(() => {
    getForecast('Lagos');
  }, []);

  const [current, ...otherDays] = retrievedForcast;
  const currentDay = current?.forecast?.day;
  const currentHours = current?.forecast?.hour;
  const conditionText = current?.condition?.text || "";
  const currentWind = Math.round(
    currentHours?.[12]?.wind_kph ?? current?.wind_kph ?? 0
  );
  const currentRain = currentDay?.daily_chance_of_rain ?? 0;
  const currentTemp = Math.round(current?.temp_c ?? 0);
  const currentHigh = Math.round(currentDay?.maxtemp_c ?? current?.temp_c ?? 0);
  const currentIcon = getWeatherEmoji(conditionText, current?.is_day ?? 1);

  const extraDays = otherDays.map((day) => ({
    key: day.date,
    day: getDayLabel(day.date),
    fullDate: getFullDateLabel(day.date),
    icon: getWeatherEmoji(day.day?.condition?.text, 1),
    high: Math.round(day.day?.maxtemp_c ?? 0),
    rain: day.day?.daily_chance_of_rain ?? 0,
    wind: Math.round(day.hour?.[12]?.wind_kph ?? day.hour?.[0]?.wind_kph ?? 0),
    condition: day.day?.condition?.text,
  }));

  return (
    <section className="wtr-section">
      <style>{`
        .wtr-section {
          position: relative;
          isolation: isolate;
          background-image: url("/assets/img/weather.jpeg");
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          padding: 70px 0;
        }
        .wtr-section::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          background: linear-gradient(
            105deg,
            rgba(3, 20, 32, 0.78) 0%,
            rgba(6, 34, 52, 0.55) 45%,
            rgba(6, 34, 52, 0.3) 100%
          );
        }
        .wtr-section .container {
          position: relative;
          z-index: 1;
        }
        .wtr-eyebrow {
          display: inline-block;
          color: #7ef0f7;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
        }
        .wtr-place {
          color: #ffffff;
          font-weight: 800;
          font-size: clamp(2rem, 5vw, 3.4rem);
          margin: 8px 0 0;
          line-height: 1.1;
          text-shadow: 0 3px 16px rgba(0, 0, 0, 0.55);
        }
        .wtr-meta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 28px;
          margin-top: 14px;
          margin-bottom: 34px;
        }
        .wtr-date,
        .wtr-updated {
          color: rgba(255, 255, 255, 0.95);
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
        }
        .wtr-updated {
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
        }
        .wtr-grid {
          display: flex;
          flex-wrap: wrap;
          align-items: stretch;
          gap: 26px;
          margin-top: 34px;
        }
        .wtr-col-left {
          flex: 1 1 48%;
          min-width: 280px;
          display: flex;
          flex-direction: column;
        }
        .wtr-col-right {
          flex: 1 1 40%;
          min-width: 240px;
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }
        .wtr-card {
          border-radius: 24px;
          padding: 26px 26px 24px;
          color: #ffffff;
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
        }
        .wtr-card--light {
          background: rgba(255, 255, 255, 0.22);
          border: 1px solid rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          margin-top: auto;
        }
        .wtr-card--teal {
          background: #0c9ea9;
          border: 1px solid rgba(255, 255, 255, 0.25);
        }
        .wtr-card-title {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.85);
          margin-bottom: 18px;
        }
        .wtr-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 34px;
        }
        .wtr-stat {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .wtr-stat-label {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.88);
        }
        .wtr-stat-value {
          font-size: 30px;
          font-weight: 800;
          line-height: 1;
          color: #ffffff;
        }
        .wtr-stat-unit {
          font-size: 14px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.8);
        }
        .wtr-temp-wrap {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }
        .wtr-temp {
          font-size: clamp(3.4rem, 7vw, 4.6rem);
          font-weight: 800;
          line-height: 1;
          color: #ffffff;
        }
        .wtr-temp sup {
          font-size: 0.4em;
          font-weight: 700;
        }
        .wtr-temp-icon {
          font-size: 58px;
          line-height: 1;
          filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.3));
        }
        .wtr-temp-meta {
          margin-top: 14px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .wtr-temp-high {
          font-size: 15px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }
        .wtr-condition {
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.85);
        }
        .wtr-more {
          margin-top: 16px;
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.16);
          border: 1px solid rgba(255, 255, 255, 0.5);
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          border-radius: 999px;
          padding: 10px 20px;
          cursor: pointer;
          transition: background 0.3s ease, transform 0.3s ease;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        }
        .wtr-more:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }
        .wtr-extra {
          margin-top: 30px;
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }
        .wtr-day {
          flex: 1 1 160px;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 18px;
          padding: 16px 18px;
          color: #ffffff;
          display: flex;
          flex-direction: column;
          gap: 8px;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
        }
        .wtr-day-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .wtr-day-name {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: #ffffff;
        }
        .wtr-day-icon {
          font-size: 24px;
          line-height: 1;
        }
        .wtr-day-high {
          font-size: 24px;
          font-weight: 800;
          line-height: 1;
          color: #ffffff;
        }
        .wtr-day-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 12px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }
        .wtr-day-condition {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.8);
        }
        .wtr-loading {
          padding: 40px 0;
        }
        .wtr-loading p {
          color: #ffffff;
          font-size: 15px;
          margin-top: 12px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
        }
        @media (max-width: 767px) {
          .wtr-grid {
            flex-direction: column;
            gap: 20px;
          }
          .wtr-col-left,
          .wtr-col-right {
            flex: 1 1 100%;
          }
          .wtr-card--light {
            margin-top: 24px;
          }
        }
      `}</style>

      <div className="container">
        <div className="text-center mb-5">
          <span className="wtr-eyebrow">Weather Forecast</span>
        </div>

        {!retrievedForcast.length ? (
          <div className="wtr-loading text-center">
            <img
              src="assets/img/loading.svg"
              alt=""
              style={{ width: 50 }}
            />
            <p>Loading weather forecast</p>
          </div>
        ) : (
          <>
            <div className="wtr-grid">
              <div className="wtr-col-left">
                <span className="wtr-eyebrow">Right now</span>
                <h2 className="wtr-place">Lagos, Nigeria</h2>
                <div className="wtr-meta">
                  <span className="wtr-date">
                    {getFullDateLabel(current?.last_updated)}
                  </span>
                  <span className="wtr-updated">
                    Updated {formatTimeLabel(current?.last_updated)}
                  </span>
                </div>

                <div className="wtr-card wtr-card--light">
                  <div className="wtr-card-title">Conditions</div>
                  <div className="wtr-stats">
                    <div className="wtr-stat">
                      <span className="wtr-stat-label">💧 Rain</span>
                      <span className="wtr-stat-value">
                        {currentRain}
                        <span className="wtr-stat-unit">%</span>
                      </span>
                    </div>
                    <div className="wtr-stat">
                      <span className="wtr-stat-label">💨 Wind</span>
                      <span className="wtr-stat-value">
                        {currentWind}
                        <span className="wtr-stat-unit"> km/h</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="wtr-col-right">
                <div className="wtr-card wtr-card--teal">
                  <div className="wtr-card-title">Temperature</div>
                  <div className="wtr-temp-wrap">
                    <span className="wtr-temp">
                      {currentTemp}°<sup>C</sup>
                    </span>
                    <span className="wtr-temp-icon" aria-hidden="true">
                      {currentIcon}
                    </span>
                  </div>
                  <div className="wtr-temp-meta">
                    <span className="wtr-temp-high">High {currentHigh}°C</span>
                    <span className="wtr-condition">{conditionText}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="wtr-more"
                  onClick={() => setShowMore((prev) => !prev)}
                >
                  {showMore ? "Show less" : "See more"}
                  <span aria-hidden="true">{showMore ? "▴" : "▾"}</span>
                </button>
              </div>
            </div>

            {showMore && (
              <div className="wtr-extra">
                {extraDays.map((day) => (
                  <div className="wtr-day" key={day.key}>
                    <div className="wtr-day-top">
                      <span className="wtr-day-name">{day.day}</span>
                      <span className="wtr-day-icon" aria-hidden="true">
                        {day.icon}
                      </span>
                    </div>
                    <span className="wtr-day-high">{day.high}°C</span>
                    <div className="wtr-day-meta">
                      <span>💨 {day.wind} km/h</span>
                      <span>💧 {day.rain}%</span>
                    </div>
                    <span className="wtr-day-condition">{day.condition}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default WeatherSlider;
